import { TestDefinition, QuestionType } from '../../types';
import { bssiScale } from './shared/scales';

export const TESTS_RIESGO_SUICIDA: TestDefinition[] = [
{
    id: 'c-ssrs-screener',
    code: 'C-SSRS',
    name: 'Columbia-Suicide Severity Rating Scale (Screener)',
    category: 'Riesgo Suicida',
    description: 'Escala de evaluación del riesgo suicida. Versión screener de 6 ítems para detección y evaluación de la severidad de la ideación y comportamiento suicida.',
    questions: [
      {
        id: 'q1',
        text: '1. Deseo de estar muerto/a: ¿Ha deseado estar muerto o poder dormirse y no despertar?',
        type: QuestionType.YES_NO,
        options: [{ label: 'No', value: 0 }, { label: 'Sí', value: 1 }]
      },
      {
        id: 'q2',
        text: '2. Pensamientos suicidas activos no específicos: ¿Ha tenido pensamientos de suicidarse?',
        type: QuestionType.YES_NO,
        options: [{ label: 'No', value: 0 }, { label: 'Sí', value: 1 }]
      },
      {
        id: 'q3',
        text: '3. Ideación suicida con métodos (sin plan específico ni intención de actuar): ¿Ha pensado en cómo podría hacer esto?',
        type: QuestionType.YES_NO,
        options: [{ label: 'No', value: 0 }, { label: 'Sí', value: 1 }]
      },
      {
        id: 'q4',
        text: '4. Ideación suicida con alguna intención de actuar (sin plan específico): ¿Ha tenido estas ideas y en cierto grado la intención de llevarlas a cabo?',
        type: QuestionType.YES_NO,
        options: [{ label: 'No', value: 0 }, { label: 'Sí', value: 1 }]
      },
      {
        id: 'q5',
        text: '5. Ideación suicida con plan específico e intención: ¿Ha empezado a elaborar los detalles sobre cómo suicidarse? ¿Tiene intención de llevar a cabo este plan?',
        type: QuestionType.YES_NO,
        options: [{ label: 'No', value: 0 }, { label: 'Sí', value: 1 }]
      },
      {
        id: 'q6',
        text: '6. Comportamiento suicida: ¿Ha hecho algo, comenzado a hacer algo o se ha preparado para hacer algo para terminar con su vida?',
        type: QuestionType.YES_NO,
        options: [{ label: 'No', value: 0 }, { label: 'Sí', value: 1 }]
      }
    ],
    interpretations: [
      { min: 0, max: 0, label: 'Sin Riesgo', color: 'bg-green-100 text-green-800' },
      { min: 1, max: 2, label: 'Riesgo Bajo', color: 'bg-yellow-100 text-yellow-800' },
      { min: 3, max: 4, label: 'Riesgo Moderado', color: 'bg-orange-100 text-orange-800' },
      { min: 5, max: 6, label: 'Riesgo Alto/Severo', color: 'bg-red-200 text-red-900' },
    ],
    alertThresholds: [
      { score: 1, condition: 'gte', severity: 'critical', message: 'C-SSRS: Ideación con método — protocolo de seguridad inmediato', questionId: 'q3' },
      { score: 3, condition: 'gte', severity: 'critical', message: 'Riesgo suicida moderado-alto (C-SSRS ≥ 3) — evaluación urgente' },
      { score: 1, condition: 'gte', severity: 'warning', message: 'Ideación suicida presente (C-SSRS ≥ 1) — monitoreo estrecho' },
    ],
  },
{
    id: 'bssi-full',
    code: 'BSSI',
    name: 'Beck Scale for Suicide Ideation (Complete)',
    category: 'Riesgo Suicida',
    description: 'Evaluación exhaustiva de la ideación suicida, planificación e intención (21 ítems).',
    excludeFromTotal: ['b20', 'b21'],
    questions: [
      { id: 'b1', text: '1. Deseo de vivir', type: QuestionType.LIKERT, options: bssiScale('Moderado a fuerte', 'Débil', 'Ninguno') },
      { id: 'b2', text: '2. Deseo de morir', type: QuestionType.LIKERT, options: bssiScale('Ninguno', 'Débil', 'Moderado a fuerte') },
      { id: 'b3', text: '3. Razones para vivir/morir', type: QuestionType.LIKERT, options: bssiScale('Vivir > Morir', 'Vivir = Morir', 'Morir > Vivir') },
      { id: 'b4', text: '4. Deseo de intento suicida activo', type: QuestionType.LIKERT, options: bssiScale('Ninguno', 'Débil', 'Moderado a fuerte') },
      { id: 'b5', text: '5. Deseo pasivo de suicidio', type: QuestionType.LIKERT, options: bssiScale('Tomaría precauciones para salvarse', 'Dejaría el morir al azar', 'Evitaría pasos para salvarse') },
      // Note: Items 6-21 continue with logic (conditional) but for this structure we list them sequentially
      { id: 'b6', text: '6. Duración de los pensamientos suicidas', type: QuestionType.LIKERT, options: bssiScale('Breve, pasajeros', 'Periodos más largos', 'Continuos o casi continuos') },
      { id: 'b7', text: '7. Frecuencia de la ideación', type: QuestionType.LIKERT, options: bssiScale('Rara/Ocasional', 'Frecuente', 'Continua') },
      { id: 'b8', text: '8. Actitud hacia la ideación', type: QuestionType.LIKERT, options: bssiScale('Rechazo', 'Ambivalencia', 'Aceptación') },
      { id: 'b9', text: '9. Control sobre la acción suicida', type: QuestionType.LIKERT, options: bssiScale('Tiene control', 'No seguro del control', 'Sin control') },
      { id: 'b10', text: '10. Disuasivos (familia, religión)', type: QuestionType.LIKERT, options: bssiScale('No intentaría por disuasivos', 'Alguna preocupación por disuasivos', 'Mínima o ninguna preocupación') },
      { id: 'b11', text: '11. Razones para el intento', type: QuestionType.LIKERT, options: bssiScale('Manipular entorno/Atención', 'Combinación', 'Escapar/Solución absoluta') },
      { id: 'b12', text: '12. Especificidad del plan', type: QuestionType.LIKERT, options: bssiScale('Ninguna', 'Considerado, no trabajado', 'Plan completo') },
      { id: 'b13', text: '13. Disponibilidad del método', type: QuestionType.LIKERT, options: bssiScale('Método no disponible', 'Toma tiempo obtenerlo', 'Disponible') },
      { id: 'b14', text: '14. Capacidad de llevar a cabo el intento', type: QuestionType.LIKERT, options: bssiScale('Sin valor/competencia', 'Inseguro de capacidad', 'Seguro de competencia') },
      { id: 'b15', text: '15. Expectativa de intento real', type: QuestionType.LIKERT, options: bssiScale('No', 'Incierto', 'Sí') },
      { id: 'b16', text: '16. Preparación del intento', type: QuestionType.LIKERT, options: bssiScale('Ninguna', 'Parcial/mínima', 'Completa') },
      { id: 'b17', text: '17. Nota suicida', type: QuestionType.LIKERT, options: bssiScale('Ninguna', 'Pensó en escribir/empezó', 'Completada') },
      { id: 'b18', text: '18. Actos finales anticipatorios (testamento, seguro)', type: QuestionType.LIKERT, options: bssiScale('Ninguno', 'Pensó en hacer/arregló algunos', 'Hizo arreglos definitivos') },
      { id: 'b19', text: '19. Engaño/Ocultamiento', type: QuestionType.LIKERT, options: bssiScale('Reveló ideas', 'Evitó revelar', 'Intentó engañar/ocultar/mentir') },
      { id: 'b20', text: '20. Resultado de intento (screener)', type: QuestionType.LIKERT, options: bssiScale('No aplicable', 'No aplicable', 'Intento en pasado') },
      { id: 'b21', text: '21. Seriedad del intento previo', type: QuestionType.LIKERT, options: bssiScale('No hubo intento', 'Manipulativo', 'Serio con intención de morir') }
    ],
    interpretations: [
      { min: 0, max: 5, label: 'Bajo Riesgo', color: 'bg-green-100 text-green-800' },
      { min: 6, max: 19, label: 'Riesgo Moderado', color: 'bg-yellow-100 text-yellow-800' },
      { min: 20, max: 28, label: 'Riesgo Alto', color: 'bg-orange-100 text-orange-800' },
      { min: 29, max: 38, label: 'Riesgo Muy Alto', color: 'bg-red-200 text-red-900' },
    ],
    alertThresholds: [
      { score: 1, condition: 'gte', severity: 'critical', message: 'BSSI: Deseo activo de intento suicida — protocolo de seguridad inmediato', questionId: 'b4' },
      { score: 20, condition: 'gte', severity: 'critical', message: 'Ideación suicida de alto riesgo (BSSI ≥ 20) — evaluación urgente' },
      { score: 6, condition: 'gte', severity: 'warning', message: 'Ideación suicida moderada (BSSI ≥ 6) — monitoreo estrecho' },
    ],
  }
];
